"use client";

import React from "react";
import NumberCard from "@/components/Cards/NumberCard";
import NumberCardSkeleton from "@/components/Cards/NumberCardSkeleton";
import { useLiveNumbers } from "@/lib/useLiveNumbers";

// Every available private number across all countries, live from Supabase,
// in the same card grid used on the per-country rent pages. With
// `guaranteeCountry` set (country pages), the section is titled as a
// guarantee and each card carries a guarantee badge — numbers work for any
// country, so visitors aren't stuck when local stock runs out. Adding
// `guaranteeService` (the number generator's results) names the service the
// visitor picked, since any private number works for any of them.

export default function AllPrivateNumbersList({
  guaranteeCountry,
  guaranteeService,
}: {
  guaranteeCountry?: string;
  guaranteeService?: string;
}) {
  const live = useLiveNumbers("all");
  const numbers = live?.privateNumbers;

  // "for {service} in {country}" on the generator, "in {country}" on country
  // pages, neither on /rent-private-number.
  const guaranteeScope = guaranteeCountry
    ? guaranteeService
      ? `for ${guaranteeService} in ${guaranteeCountry}`
      : `in ${guaranteeCountry}`
    : null;

  const title = numbers
    ? guaranteeScope
      ? `We have ${numbers.length} numbers available guaranteed to work ${guaranteeScope}`
      : numbers.length > 0
        ? `${numbers.length} Private Numbers Available to Rent Right Now`
        : "No Private Numbers Available Right Now"
    : guaranteeScope
      ? `Numbers available guaranteed to work ${guaranteeScope}`
      : "Private Numbers Available to Rent Right Now";

  return (
    <div className="py-8 space-y-6">
      <h2 className="text-3xl font-bold text-gray-900">{title}</h2>

      <div className="gap-4 w-full h-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
        {numbers
          ? numbers.map((number: any) => (
              <NumberCard
                isPrivate={true}
                key={number.id}
                country={`${number.countries?.country_flag ?? ""} ${
                  number.countries?.country_name ?? ""
                }`}
                status={number.status.toLowerCase()}
                number={`+${number.countries?.country_tel_code ?? ""}-${
                  number.number
                }`}
                href={`/account/rent-number?pn=${number.id}`}
                price={number.week_price}
                guaranteeText={
                  guaranteeService && guaranteeCountry
                    ? `Private number guaranteed to work for ${guaranteeService} in ${guaranteeCountry}`
                    : guaranteeCountry
                      ? `Guaranteed to work in ${guaranteeCountry}`
                      : undefined
                }
              />
            ))
          : Array.from({ length: 4 }).map((_, i) => (
              <NumberCardSkeleton key={i} />
            ))}
      </div>
    </div>
  );
}
